import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../state/auth/useAuth';
import { patientAPI, familyAPI } from '../../api/patient';
import appointmentAPI from '../../api/appointment';
import { formatISTRange } from './utils/date';
import userAPI from '../../api/user';
import { useToast } from '../../hooks/useToast';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../../styles/custom-toast.css';
import crossTab from '../../utils/crossTabCommunication';
import { useSocketContext } from '../../context/SocketContext';

// Component imports
import TopNavBar from './components/shared/TopNavBar';
import CalendarSidebar from './components/shared/CalendarSidebar';
import AppointmentManagement from './components/AppointmentManagement';
import BookingRequests from './components/BookingRequests';
import PatientRegistration from './components/PatientRegistration_New';
import CheckInOut from './components/CheckInOut';
import PatientDetails from './components/PatientDetails';
import Billing from './components/Billing';
import ClaimFormB from './components/ClaimFormB';
import Communication from './components/Communication';
import PatientEMR from './components/PatientEMR';
import ProfileManagement from './components/ProfileManagement';
import { DASHBOARD_TABS } from './utils/constants';
import NavigationPreventer from '../../components/NavigationPreventer';

const ReceptionistDashboard = () => {
  const { user, logout } = useAuth();
  const { toasts, showSuccess, showError, showWarning, showInfo, removeToast } = useToast();
  const { addEventListener, removeEventListener, isConnected } = useSocketContext();
  const [activeTab, setActiveTab] = useState('appointment-management');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Registration form state
  const [registrationStep, setRegistrationStep] = useState(1);
  const [hasFamilyMember, setHasFamilyMember] = useState(false);
  const [familyAlreadyRegistered, setFamilyAlreadyRegistered] = useState(false);
  const [createdPatientId, setCreatedPatientId] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [appointmentsByDoctor, setAppointmentsByDoctor] = useState({});
  // Pagination state
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [currentPage, setCurrentPage] = useState(1);
  // Date range filter state (YYYY-MM-DD)
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Debug effect to monitor patient search state in parent component
  useEffect(() => {
    console.log('🔍 [PARENT] Patient search state changed:', {
      patientSearchResults,
      showPatientSearch,
      resultsCount: patientSearchResults?.length
    });
  }, [patientSearchResults, showPatientSearch]);

  const [appointmentData, setAppointmentData] = useState({
    patient: '',
    patientName: '',
    doctor: '',
    appointmentDate: '',
    appointmentTime: '',
    duration: 30,
    purpose: '',
    note: '',
    status: 'Scheduled'
  });

  const [patientData, setPatientData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    sex: '',
    dob: '',
    bloodGroup: '',
    drugAllergies: [],
    pastHistory: '',
    allergies: [],
    currentMedications: [],
    medicalHistory: '',
    personalAddress: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    officeDetail: {
      company: '',
      designation: '',
      officeAddress: ''
    },
    vitals: {
      heightCm: '',
      weightKg: '',
      bmi: '',
      bpSys: '',
      bpDia: '',
      pulse: '',
      temperature: '',
      oxygenSaturation: '',
      respiration: ''
    },
    assignedDoctor: '',
    medicalNotes: ''
  });

  const [patientPhoto, setPatientPhoto] = useState(null);

  const [familyData, setFamilyData] = useState({
    name: '',
    headName: '',
    phone: '',
    area: '',
    city: '',
    village: ''
  });

  const [familyMemberData, setFamilyMemberData] = useState({
    relationshipType: '',
    searchTerm: ''
  });

  const [vitalsData, setVitalsData] = useState({
    bloodPressure: {
      systolic: '',
      diastolic: ''
    },
    heartRate: '',
    temperature: '',
    respiratoryRate: '',
    oxygenSaturation: '',
    height: '',
    weight: '',
    notes: ''
  });

  // Helper: add days to a YYYY-MM-DD string and return YYYY-MM-DD (timezone safe)
  const addDaysStr = (dateStr, days) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const local = new Date(y, (m - 1), d, 0, 0, 0, 0); // local midnight
    local.setDate(local.getDate() + days);
    const yyyy = local.getFullYear();
    const mm = String(local.getMonth() + 1).padStart(2, '0');
    const dd = String(local.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Generic loader for current dateFrom/dateTo state - wrapped in useCallback for stable reference
  const loadAppointmentsForCurrentRange = useCallback(async (fromOverride, toOverride, options = {}) => {
    // Guard against React passing a click event as the first arg
    const fromIsEventLike = fromOverride && typeof fromOverride === 'object' && ('nativeEvent' in fromOverride || 'target' in fromOverride);
    const toIsEventLike = toOverride && typeof toOverride === 'object' && ('nativeEvent' in toOverride || 'target' in toOverride);

    const cleanedFrom = fromIsEventLike ? undefined : fromOverride;
    const cleanedTo = toIsEventLike ? undefined : toOverride;

    const effectiveFrom = typeof cleanedFrom === 'string' && cleanedFrom ? cleanedFrom : dateFrom;
    const effectiveTo = typeof cleanedTo === 'string' && cleanedTo ? cleanedTo : dateTo;
    const page = options.page || currentPage;
    console.log('🔄 Loading appointments for range:', { from: effectiveFrom, to: effectiveTo, page });
    setLoading(true);
    try {
      setError('');
      // Backends commonly use end-exclusive range; use to+1 day for safety
      const toExclusive = addDaysStr(effectiveTo, 1);
      const response = await appointmentAPI.list({
        from: effectiveFrom,
        to: toExclusive,
        includePatient: true,
        includeDoctor: true,
        page: page,
        limit: 10
      });
           
      // Handle different response structures
      let appointmentsData = [];
      let paginationData = { page: 1, limit: 10, total: 0, pages: 1 };
      
      if (response && response.data?.items) {
        appointmentsData = response.data.items;
        if (response.data.pagination) {
          paginationData = response.data.pagination;
        }
      } else if (Array.isArray(response?.items)) {
        appointmentsData = response.items;
        if (response.pagination) {
          paginationData = response.pagination;
        }
      } else if (Array.isArray(response)) {
        appointmentsData = response; // fallback if API returns array directly
      } else {
        console.warn('Unexpected response format:', response);
        appointmentsData = [];
      }
      
      console.log(`🔄 Loaded ${appointmentsData.length} appointments:`, appointmentsData);
      console.log('📄 Pagination:', paginationData);
      setAppointments(appointmentsData);
      setPagination(paginationData);

      // Group appointments by doctor
      const groupedAppointments = appointmentsData.reduce((acc, appointment) => {
        const doctorId = appointment.doctor?._id || appointment.doctor?.id || appointment.doctorId || 'unknown';
        const doctorName = appointment.doctor?.name || appointment.doctorName || 'Unknown Doctor';
        const doctorSpecialty = appointment.doctor?.specialty || 'General';

        if (!acc[doctorId]) {
          acc[doctorId] = {
            doctor: {
              id: doctorId,
              name: doctorName,
              specialty: doctorSpecialty
            },
            appointments: []
          };
        }

        acc[doctorId].appointments.push(appointment);
        return acc;
      }, {});

      console.log('🔄 Grouped appointments by doctor:', groupedAppointments);
      setAppointmentsByDoctor(groupedAppointments);
      
      // Removed automatic info toast for loading appointments - users don't need this notification
      // Only show toast on manual refresh or specific user actions
      // if (!options.silent) {
      //   if (appointmentsData.length > 0) {
      //     showInfo(`Loaded ${appointmentsData.length} appointment${appointmentsData.length > 1 ? 's' : ''}`);
      //   }
      // }
    } catch (error) {
      console.error('Failed to load appointments:', error);
      setError(`Failed to load appointments: ${error.message}`);
      showError(`Failed to load appointments: ${error.message}`);
      // Set empty state on error
      setAppointments([]);
      setAppointmentsByDoctor({});
      setPagination({ page: 1, limit: 10, total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, currentPage, showInfo, showError]); // useCallback dependencies

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
    loadAppointmentsForCurrentRange(dateFrom, dateTo, { page: newPage });
  }, [dateFrom, dateTo, loadAppointmentsForCurrentRange]);

  // Load appointments on component mount and set up auto-refresh
  useEffect(() => {
    // Initial load only (silent to avoid toast spam on mount)
    loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
    loadDoctors();
  }, [loadAppointmentsForCurrentRange]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Socket event listeners for real-time appointment updates
  useEffect(() => {
    if (!isConnected || !addEventListener || !removeEventListener) {
      return;
    }

    // Handler for new appointment bookings (real-time refresh)
    const handleAppointmentBooked = (data) => {
      console.log('🏥 [Dashboard] New appointment booked via socket:', data);
      
      // Only auto-refresh if the appointment is for today or within current date range
      const appointmentDate = new Date(data.appointment?.dateTime);
      const currentFromDate = new Date(dateFrom);
      const currentToDate = new Date(dateTo);
      
      // Add one day to currentToDate for end-inclusive comparison
      const endDateInclusive = new Date(currentToDate);
      endDateInclusive.setDate(endDateInclusive.getDate() + 1);
      
      // Check if appointment falls within current date filter
      if (appointmentDate >= currentFromDate && appointmentDate < endDateInclusive) {
        console.log('✅ Appointment is within current date range, refreshing immediately...');
        // Refresh appointments immediately to show the new appointment
        loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
      } else {
        console.log('⏭️ Appointment is outside current date range, skipping refresh');
      }
    };

    // Handler for queue updates
    const handleQueueUpdated = (data) => {
      console.log('🏥 [Dashboard] Queue updated:', data);
      
      // Refresh appointments silently to update statuses
      setTimeout(() => {
        loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
      }, 500);
    };

    // Handler for patient added to queue
    const handlePatientAddedToQueue = (data) => {
      console.log('🏥 [Dashboard] Patient added to queue:', data);
      
      // Refresh appointments silently to update queue status
      setTimeout(() => {
        loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
      }, 500);
    };

    // Handler for service started
    const handleServiceStarted = (data) => {
      console.log('🏥 [Dashboard] Service started:', data);
      
      // Refresh appointments silently to update status
      setTimeout(() => {
        loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
      }, 500);
    };

    // Handler for service completed
    const handleServiceCompleted = (data) => {
      console.log('🏥 [Dashboard] Service completed:', data);
      
      // Refresh appointments silently to update status
      setTimeout(() => {
        loadAppointmentsForCurrentRange(undefined, undefined, { silent: true });
      }, 500);
    };

    // Register socket event listeners
    addEventListener('appointment:booked', handleAppointmentBooked);
    addEventListener('queue:updated', handleQueueUpdated);
    addEventListener('queue:patient-added', handlePatientAddedToQueue);
    addEventListener('queue:service-started', handleServiceStarted);
    addEventListener('queue:service-completed', handleServiceCompleted);

    console.log('🔌 [Dashboard] Socket event listeners registered');

    // Cleanup on unmount
    return () => {
      removeEventListener('appointment:booked', handleAppointmentBooked);
      removeEventListener('queue:updated', handleQueueUpdated);
      removeEventListener('queue:patient-added', handlePatientAddedToQueue);
      removeEventListener('queue:service-started', handleServiceStarted);
      removeEventListener('queue:service-completed', handleServiceCompleted);
      console.log('🔌 [Dashboard] Socket event listeners cleaned up');
    };
  }, [isConnected, addEventListener, removeEventListener, dateFrom, dateTo, loadAppointmentsForCurrentRange]);

  // Convenience: set range to today and fetch
  const loadTodaysAppointments = async () => {
    const today = new Date().toISOString().split('T')[0];
    setDateFrom(today);
    setDateTo(today);
    // Pass explicit overrides to ensure correct range immediately
    await loadAppointmentsForCurrentRange(today, today);
  };

  // Handler for applying date filter from child component
  const handleApplyDateFilter = async (from, to) => {
    try {
      if (!from || !to) {
        showError('Please select both From and To dates');
        return;
      }
      if (new Date(from) > new Date(to)) {
        showError('From date cannot be after To date');
        return;
      }
      setDateFrom(from);
      setDateTo(to);
      await loadAppointmentsForCurrentRange(from, to);
    } catch (e) {
      console.error('Failed applying date filter:', e);
      showError('Failed to apply date filter');
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await userAPI.getDoctors();
      console.log('Doctors API Response abc :', response.users);
      
      // Handle different response structures
      let doctorsData = [];
      if (response && response.users) {
        doctorsData = response.users;
      } else {
        console.warn('Unexpected doctors response format:', response);
        doctorsData = [];
      }
      
      setDoctors(doctorsData);
      // Removed the info toast for loading doctors - it's not necessary and annoying
      // showInfo(`Loaded ${doctorsData.length} doctor${doctorsData.length > 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Failed to load doctors:', error);
      showError('Failed to load doctors, using fallback data');
      // Set mock doctors as fallback
      setDoctors([
        { _id: '1', id: '1', name: 'Dr. Smith', specialty: 'General Medicine' },
        { _id: '2', id: '2', name: 'Dr. Johnson', specialty: 'Cardiology' },
        { _id: '3', id: '3', name: 'Dr. Williams', specialty: 'Pediatrics' }
      ]);
    }
  };

  // Complete an appointment
  const handleCompleteAppointment = async (appointmentId) => {
    setLoading(true);
    try {
      await appointmentAPI.complete(appointmentId);
      showSuccess('Appointment completed successfully');
      await loadTodaysAppointments();
    } catch (error) {
      console.error('Failed to complete appointment:', error);
      setError('Failed to complete appointment');
      showError('Failed to complete appointment');
    } finally {
      setLoading(false);
    }
  };

  // Cancel an appointment
  const handleCancelAppointment = async (appointmentId) => {
    setLoading(true);
    try {
      await appointmentAPI.cancel(appointmentId);
      showSuccess('Appointment cancelled successfully');
      await loadTodaysAppointments();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      setError('Failed to cancel appointment');
      showError('Failed to cancel appointment');
    } finally {
      setLoading(false);
    }
  };

  // Start appointment service (Scheduled -> InProgress)
  const handleStartAppointment = async (appointmentId) => {
    setLoading(true);
    try {
      await appointmentAPI.startService(appointmentId);
      showSuccess('Appointment started successfully');
      await loadTodaysAppointments();
    } catch (error) {
      console.error('Failed to start appointment:', error);
      setError('Failed to start appointment');
      showError('Failed to start appointment');
    } finally {
      setLoading(false);
    }
  };

  // Join appointment queue (Scheduled -> InQueue)
  const handleJoinQueue = async (appointmentId) => {
    setLoading(true);
    try {
      const response = await appointmentAPI.joinQueue(appointmentId);
      
      // Trigger cross-tab notification for doctor dashboards
      if (response?.data?.appointment) {
        const appointment = response.data.appointment;
        const doctorId = appointment.doctor?._id || appointment.doctor;
        
        if (doctorId) {
          crossTab.notifyQueueRefresh(doctorId);
        }
      }
      
      showSuccess('Patient added to queue successfully');
      await loadTodaysAppointments();
    } catch (error) {
      console.error('Failed to join queue:', error);
      setError('Failed to join queue');
      showError('Failed to join queue');
    } finally {
      setLoading(false);
    }
  };

  const resetRegistrationForm = () => {
    setRegistrationStep(1);
    setHasFamilyMember(false);
    setFamilyAlreadyRegistered(false);
    setCreatedPatientId(null);
    setSearchResults([]);
    setError('');
    setPatientPhoto(null);
    setPatientData({
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      sex: '',
      dob: '',
      bloodGroup: '',
      drugAllergies: [],
      pastHistory: '',
      allergies: [],
      currentMedications: [],
      medicalHistory: '',
      personalAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      officeDetail: {
        company: '',
        designation: '',
        officeAddress: ''
      },
      vitals: {
        heightCm: '',
        weightKg: '',
        bmi: '',
        bpSys: '',
        bpDia: '',
        pulse: '',
        temperature: '',
        oxygenSaturation: '',
        respiration: ''
      },
      assignedDoctor: '',
      medicalNotes: ''
    });
    setFamilyData({
      name: '',
      headName: '',
      phone: '',
      area: '',
      city: '',
      village: ''
    });
    setFamilyMemberData({
      relationshipType: '',
      searchTerm: ''
    });
    setVitalsData({
      bloodPressure: {
        systolic: '',
        diastolic: ''
      },
      heartRate: '',
      temperature: '',
      respiratoryRate: '',
      oxygenSaturation: '',
      height: '',
      weight: '',
      notes: ''
    });
  };

  // Handle patient data collection (Step 1)
  const handlePatientDataStep = async () => {
    setLoading(true);
    try {
      const requiredFields = ['firstName', 'lastName', 'mobile', 'dob', 'sex', 'personalAddress.street'];
      const isValid = requiredFields.every(field => {
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          return patientData[parent]?.[child]?.trim();
        }
        return patientData[field]?.trim();
      });

      if (!isValid) {
        setError('Please fill in all required fields');
        return;
      }

      setError('');
      setRegistrationStep(2);
    } catch (error) {
      console.error('Patient data validation failed:', error);
      setError('Failed to validate patient data');
    } finally {
      setLoading(false);
    }
  };

  // Handle patient creation and complete registration (Step 2)
  const handleCompleteRegistration = async () => {
    setLoading(true);
    try {
      setError('');

      // Use the API method to create patient with photo and vitals
      const createdPatient = await patientAPI.createWithPhoto(
        patientData, 
        patientPhoto, 
        vitalsData
      );

      setCreatedPatientId(createdPatient._id || createdPatient.id);

      // Success - reset form and show success message
      showSuccess('Patient registration completed successfully!');
      resetRegistrationForm();
      setActiveTab('appointment-management');
    } catch (error) {
      console.error('Registration completion failed:', error);
      const status = error?.response?.status;
      if (status === 409) {
        // Friendly duplicate message; suppress top-right toast
        setError('Patient with the same name and date of birth already exists.');
      } else {
        const errorMessage = 'Failed to complete registration: ' + (error.message || 'Unknown error');
        setError(errorMessage);
        // showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle family search
  const handleFamilySearch = async () => {
    setLoading(true);
    try {
      if (!familyMemberData.searchTerm.trim()) {
        const errorMsg = 'Please enter search term';
        setError(errorMsg);
        showWarning(errorMsg);
        return;
      }

      const families = await familyAPI.search({ q: familyMemberData.searchTerm });
      const results = families?.data?.items || families?.items || families?.data || families || [];
      setSearchResults(results);
      setError('');
      
      if (results.length === 0) {
        showInfo('No families found matching your search');
      } else {
        showSuccess(`Found ${results.length} matching families`);
      }
    } catch (error) {
      console.error('Family search failed:', error);
      const errorMsg = 'Failed to search families';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle appointment scheduling
  const handleScheduleAppointment = async (appointmentDataToSchedule) => {
    setLoading(true);
    try {
      setError('');

      const response = await appointmentAPI.schedule(appointmentDataToSchedule);
      
      if (response.success) {
        showSuccess('Appointment scheduled successfully!');
        // Reload appointments to reflect the new appointment immediately
        await loadTodaysAppointments();
        setError('');
        return { success: true };
      } else {
        const errorMsg = response.message || 'Failed to schedule appointment';
        setError(errorMsg);
        showError(errorMsg);
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Failed to schedule appointment:', error);
      
      let errorMsg = '';
      // Check if we have validation errors from the backend
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.map(err => `${err.field || 'Field'}: ${err.message || err}`);
        errorMsg = `Validation failed: ${errorMessages.join(', ')}`;
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else {
        errorMsg = `Failed to schedule appointment: ${error.message}`;
      }
      
      setError(errorMsg);
      showError(errorMsg);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Handle patient search for appointments
  const handlePatientSearch = useCallback(async (searchTerm) => {
    if (!String(searchTerm).trim()) {
      setPatientSearchResults([]);
      setShowPatientSearch(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 Searching for patients with term:', searchTerm);
      const response = await patientAPI.searchPatients(searchTerm);
      console.log('🔍 Full API response:', response);
      
      const patients = response.data?.patients || [];
      console.log('🔍 Extracted patients:', patients);
      console.log('🔍 Patients count:', patients.length);
      console.log('🔍 Is patients an array?', Array.isArray(patients));
      
      // Use setTimeout to ensure state updates happen after any potential clearing
      setTimeout(() => {
        setPatientSearchResults(patients);
        setShowPatientSearch(true);
        console.log('🔍 State updated via setTimeout - results:', patients.length, 'showSearch:', true);
      }, 10);
      
    } catch (error) {
      console.error('Patient search failed:', error);
      setPatientSearchResults([]);
      setShowPatientSearch(false);
      const errorMsg = 'Failed to search patients';
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [showInfo, showError]);

  // Create a stable function for updating appointment data
  const updateAppointmentData = useCallback((field, value) => {
    setAppointmentData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Create a stable function for setting appointment data object
  const setAppointmentDataStable = useCallback((newData) => {
    setAppointmentData(newData);
  }, []);

  // Fetch available slots for a doctor on a specific date
  const fetchAvailableSlots = useCallback(async (doctorOrId, date) => {
    try {
      // Import availability API
      const { getDoctorSlots } = await import('../../api/availability');
      
      // Extract doctor ID from object or use as string
      const doctorId = typeof doctorOrId === 'object' && doctorOrId !== null
        ? (doctorOrId._id || doctorOrId.id)
        : doctorOrId;

      if (!doctorId) {
        console.error('Doctor ID is required');
        return [];
      }
      
      // Ensure proper date format
      const dateStr = date instanceof Date 
        ? date.toISOString().split('T')[0] 
        : date;
      
      const response = await getDoctorSlots(doctorId, dateStr);
      
      if (response.success && response.data?.slots) {
        return response.data.slots
          .filter(slot => slot.isAvailable)
          .map(slot => ({
            ...slot,
            formattedTime: slot.startTime && slot.endTime 
              ? formatISTRange(slot.startTime, slot.endTime)
              : slot.startTime
          }));
      } else {
        // Return mock data for development
        return [];
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
      // Return mock slots for development
      return generateMockSlots();
    }
  }, []);

  // Generate mock slots for development
  const generateMockSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = 17;
    
    for (let hour = startHour; hour < endHour; hour++) {
      // Skip lunch hour
      if (hour === 13) continue;
      
      for (let minute = 0; minute < 60; minute += 30) {
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endHour24 = minute === 30 ? hour + 1 : hour;
        const endMinute = minute === 30 ? 0 : 30;
        const endTime = `${endHour24.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
        
        slots.push({
          id: `mock-slot-${hour}-${minute}`,
          startTime,
          endTime,
          formattedTime: formatISTRange(startTime, endTime),
          isAvailable: Math.random() > 0.3, // 70% availability
          duration: 30
        });
      }
    }
    
    return slots;
  };

  const renderTabContent = () => {
    const commonProps = {
      loading,
      error,
      setError
    };

    switch (activeTab) {
      case 'appointment-management':
        return (
          <AppointmentManagement
            {...commonProps}
            appointments={appointments}
            appointmentsByDoctor={appointmentsByDoctor}
            doctors={doctors}
            dateFrom={dateFrom}
            dateTo={dateTo}
            appointmentData={appointmentData}
            setAppointmentData={setAppointmentDataStable}
            updateAppointmentData={updateAppointmentData}
            fetchAvailableSlots={fetchAvailableSlots}
            onScheduleAppointment={handleScheduleAppointment}
            onRefreshAppointments={loadAppointmentsForCurrentRange}
            onApplyDateFilter={handleApplyDateFilter}
            onPatientSearch={handlePatientSearch}
            patientSearchResults={patientSearchResults}
            showPatientSearch={showPatientSearch}
            setShowPatientSearch={setShowPatientSearch}
            showScheduleModal={showScheduleModal}
            setShowScheduleModal={setShowScheduleModal}
            pagination={pagination}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSelectPatient={(patient) => {
              setAppointmentData({
                ...appointmentData,
                patient: patient._id || patient.id,
                patientName: `${patient.firstName || patient.name || ''} ${patient.lastName || ''}`.trim()
              });
              setShowPatientSearch(false);
            }}
          />
        );

      case 'booking-requests':
        return (
          <BookingRequests
            onConvertToAppointment={(request) => {
              // Pre-fill appointment data from booking request
              const firstName = request.patient?.firstName || request.firstName || '';
              const lastName = request.patient?.lastName || request.lastName || '';
              const mobile = request.patient?.mobile || request.mobile || '';
              
              setAppointmentData({
                ...appointmentData,
                patientName: `${firstName} ${lastName}`.trim(),
                appointmentDate: request.preferredDate,
                purpose: request.reason || '',
                note: `Booking Ref: ${request.referenceNumber} | Mobile: ${mobile}`,
              });
              setActiveTab('appointment-management');
              setShowScheduleModal(true);
            }}
          />
        );

      case 'patient-registration':
        return (
          <PatientRegistration
            {...commonProps}
            registrationStep={registrationStep}
            setRegistrationStep={setRegistrationStep}
            patientData={patientData}
            setPatientData={setPatientData}
            patientPhoto={patientPhoto}
            setPatientPhoto={setPatientPhoto}
            vitalsData={vitalsData}
            setVitalsData={setVitalsData}
            doctors={doctors}
            onPatientDataStep={handlePatientDataStep}
            onCompleteRegistration={handleCompleteRegistration}
            onResetRegistrationForm={resetRegistrationForm}
          />
        );

      case 'check-in':
        return (
          <CheckInOut 
            {...commonProps}
            appointments={appointments}
            appointmentsByDoctor={appointmentsByDoctor}
            onCompleteAppointment={handleCompleteAppointment}
            onCancelAppointment={handleCancelAppointment}
            onStartAppointment={handleStartAppointment}
            onJoinQueue={handleJoinQueue}
            onRefreshAppointments={loadAppointmentsForCurrentRange}
          />
        );

      case 'billing':
        return <Billing {...commonProps} />;

      case 'insurance-claim':
        return (
          <ClaimFormB 
            showToast={(type, message) => {
              if (type === 'success') showSuccess(message);
              else if (type === 'error') showError(message);
              else if (type === 'warning') showWarning(message);
              else if (type === 'info') showInfo(message);
            }}
          />
        );

      case 'communication':
        return <Communication {...commonProps} />;

      case 'patient-details':
        return (
          <PatientDetails />
        );

      case 'patient-emr':
        return (
          <PatientEMR 
            {...commonProps}
          />
        );

      case 'profile-management':
          return <ProfileManagement />;

      default:
        // Debug fallback
        if (activeTab === 'profile-management') {
          return <ProfileManagement />;
        }
        
        return (
          <div className="flex-1 p-8">
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <div className="text-6xl mb-4">🚧</div>
              <h2 className="text-2xl font-display text-theme-heading mb-2">Feature Coming Soon</h2>
              <p className="text-theme-text">This feature ({activeTab}) is under development and will be available soon.</p>
            </div>
          </div>
        );
    }
  };

  // State for calendar sidebar
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <NavigationPreventer />
      
      {/* Top Navigation Bar */}
      <TopNavBar 
        tabs={DASHBOARD_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        user={user}
        onLogout={logout}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Calendar Sidebar - Only show for appointment-management */}
        {activeTab === 'appointment-management' && (
          <CalendarSidebar
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={setDateFrom}
            onDateToChange={setDateTo}
            onRefresh={() => loadAppointmentsForCurrentRange(dateFrom, dateTo)}
            onScheduleClick={() => setShowScheduleModal(true)}
            loading={loading}
          />
        )}

        {/* Right Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 bg-theme-error/20 border border-theme-error rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-theme-error" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-800">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    onClick={() => setError('')}
                    className="text-theme-error hover:text-red-700 focus:outline-none"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-6">
            {renderTabContent()}
          </main>
        </div>
      </div>
      
      {/* React Toastify Container */}
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{
          fontFamily: 'Poppins, sans-serif',
        }}
      />
    </div>
  );
};

export default ReceptionistDashboard;


